/**
 * FP3-030 — Reset password page.
 *
 * Public page. Reads `?token=<raw>` from the URL, validates the new
 * password, calls /api/auth/reset-password, and on success the backend
 * sets the session cookie and we land the user on /.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing a token.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword({ token, password });
      if (refresh) await refresh();
      toast.success("Password updated");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "This reset link is invalid or has expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-[#F7F5EF] body-font flex items-center justify-center px-5 py-10"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-bold heading-font">F</span>
          </div>
          <div className="leading-tight">
            <div className="text-[17px] font-bold text-[#1E3F33] heading-font tracking-tight">
              Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
              Provider workspace
            </div>
          </div>
        </div>

        <Card
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-7 sm:p-8">
            <div
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
            >
              <ShieldCheck size={11} weight="duotone" />
              Account recovery
            </div>
            <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
              Reset your password
            </h1>
            <p className="text-[12.5px] text-[#6B7280] mt-1.5">
              Choose a new password to sign in.
            </p>

            <form
              data-testid="reset-password-form"
              onSubmit={onSubmit}
              className="space-y-4 mt-6"
              noValidate
            >
              <div>
                <Label
                  htmlFor="rp-password"
                  className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                >
                  New password
                </Label>
                <PasswordInput
                  id="rp-password"
                  data-testid="reset-password-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </div>
              <div>
                <Label
                  htmlFor="rp-confirm"
                  className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                >
                  Confirm password
                </Label>
                <PasswordInput
                  id="rp-confirm"
                  data-testid="reset-password-confirm"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </div>

              {error && (
                <div
                  data-testid="reset-password-error"
                  role="alert"
                  className="text-[12.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2 leading-relaxed"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="reset-password-submit"
                disabled={submitting || !token}
                className="w-full h-10 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
              >
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </form>

            <p className="mt-5 text-[11.5px] text-[#6B7280]">
              <Link
                to="/login"
                className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
              >
                ← Back to sign in
              </Link>
            </p>
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-[#9AA0A6] mt-5">
          © Frame Practice · invite-only provider workspace
        </p>
      </div>
    </div>
  );
}
